import classNames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import heroProductPreview from 'assets/images/hero-product-preview.png';
import { CtaButton } from './components/CtaButton';
import { HeroBadge } from './components/HeroBadge';
import { HeroStarfield } from './components/HeroStarfield';

const LockGlyph = () => (
  <svg viewBox="0 0 24 24" className={styles.badgeGlyph} aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
);

// Memory-keeping use case — hero.
export const Section26 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      {/* Starfield paints the dark hero; kept mounted (visibility, not
          display) so its canvas keeps a real size across theme flips. */}
      <HeroStarfield className="invisible marketing-dark:visible" />

      {/* Light-theme glow: soft pastel-blue blobs behind the hero. */}
      <div className={styles.lightGlow} aria-hidden="true" />

      <div className={styles.wrap}>
        <div className={styles.copy}>
          <HeroBadge
            icon={<LockGlyph />}
            iconClassName="bg-mf-blue text-white"
            label={t('pages.useCases.memoryKeeping.hero.badge')}
          />
          <h1 className={styles.title}>
            {t.rich('pages.useCases.memoryKeeping.hero.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h1>
          <p className={styles.sub}>
            {t('pages.useCases.memoryKeeping.hero.sub')}
          </p>
          <div className={styles.cta}>
            <CtaButton
              variant="primary"
              href={appRoutes.LOGIN}
              reportLocation="Memory Keeping Hero"
              withArrow
            >
              {t('pages.useCases.memoryKeeping.hero.ctaSave')}
            </CtaButton>
            <CtaButton variant="outline" href="#how">
              {t('pages.useCases.memoryKeeping.hero.ctaHow')}
            </CtaButton>
          </div>
          <p className={styles.note}>
            <svg
              viewBox="0 0 24 24"
              className={styles.noteIcon}
              aria-hidden="true"
            >
              <path d="m5 12 5 5L20 7" />
            </svg>
            {t('pages.useCases.memoryKeeping.hero.note')}
          </p>
        </div>

        <div className={styles.stage}>
          <Image
            src={heroProductPreview}
            alt={t('pages.useCases.memoryKeeping.hero.imageAlt')}
            className={styles.shot}
            sizes="(max-width: 1024px) 100vw, 620px"
            priority
          />
        </div>
      </div>
    </section>
  );
};

const styles = {
  section:
    'relative overflow-hidden marketing-dark:bg-mf-navy pt-[140px] pb-[90px] lg:pt-[160px]',
  lightGlow: classNames(
    'pointer-events-none absolute left-1/2 top-[-120px] h-[800px] w-[1400px] max-w-none -translate-x-1/2',
    'bg-[radial-gradient(46%_58%_at_34%_0%,rgba(189,222,255,0.6)_0%,transparent_65%),radial-gradient(40%_52%_at_70%_4%,rgba(189,222,255,0.45)_0%,transparent_62%)]',
    'marketing-dark:hidden',
  ),
  wrap: 'relative z-[2] mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-[480px_1fr] lg:gap-16 lg:px-10',
  copy: 'text-center lg:text-left',
  badgeGlyph: 'h-3 w-3 fill-none stroke-current stroke-2',
  title:
    'mx-auto max-w-[620px] font-inter text-4xl font-bold leading-[1.08] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-5xl lg:mx-0 lg:text-[56px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  sub: 'mx-auto mt-[22px] max-w-[540px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-xl',
  cta: 'mt-8 flex flex-wrap justify-center gap-[13px] lg:justify-start',
  note: 'mt-6 flex items-center justify-center gap-2 text-[15px] font-semibold text-mf-gray-600 marketing-dark:text-white/80 lg:justify-start',
  noteIcon: classNames(
    'h-[18px] w-[18px] flex-shrink-0 fill-none stroke-2',
    'stroke-[#2BA471] marketing-dark:stroke-[#6ed7a6]',
  ),
  stage: 'relative',
  shot: classNames(
    'relative z-[2] h-auto w-full rounded-[14px] border',
    'border-[#F0F0F0] shadow-[0_20px_50px_rgba(20,30,60,0.08)]',
    'marketing-dark:border-white/10',
    'marketing-dark:shadow-[0_50px_110px_rgba(2,8,30,0.66),0_8px_24px_rgba(0,0,0,0.34)]',
  ),
};
